import { VISIT_STATUSES, VISIT_TYPES } from '@wodi/domain';
import { z } from 'zod';

import { QUEUE_STATUSES } from './visits.ts';

// Home, the Reception "Today" panel and the Settings overview
// (docs/plans/phase1-home-and-demo-data.md §2). Read-only summaries.

// --- Today's visits ------------------------------------------------------------------------

export const todayVisitsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: z.uuid().optional(),
});

export const todayVisitSchema = z.object({
  id: z.uuid(),
  /** Shown in the mono face; never in a URL. */
  visitNumber: z.string(),
  patient: z.object({
    id: z.uuid(),
    /** Shown in the mono face; never in a URL. */
    patientNumber: z.string(),
    displayName: z.string(),
  }),
  type: z.enum(VISIT_TYPES),
  status: z.enum(VISIT_STATUSES),
  /** Arrival, the clinical event time. */
  startedAt: z.iso.datetime(),
  /** The visit's latest queue entry: where the patient is now, or was last. */
  queue: z
    .object({
      servicePointName: z.string(),
      status: z.enum(QUEUE_STATUSES),
    })
    .nullable(),
});

export type TodayVisit = z.infer<typeof todayVisitSchema>;

export const todayVisitsSchema = z.object({
  /** The East Africa calendar day listed, `YYYY-MM-DD`. */
  day: z.iso.date(),
  counts: z.object({
    total: z.number().int().nonnegative(),
    open: z.number().int().nonnegative(),
    closed: z.number().int().nonnegative(),
    cancelled: z.number().int().nonnegative(),
  }),
  /** Visits at this facility that arrived before today and are still open. */
  openFromEarlierDays: z.number().int().nonnegative(),
  items: z.array(todayVisitSchema),
  nextCursor: z.uuid().nullable(),
});

export type TodayVisitsView = z.infer<typeof todayVisitsSchema>;

// --- Setup status ----------------------------------------------------------------------------

export const SETUP_ITEM_KEYS = [
  'facilities',
  'service_points',
  'number_formats',
  'services',
  'prices',
  'payers',
  'stores',
  'users',
  'terminology',
] as const;
export type SetupItemKey = (typeof SETUP_ITEM_KEYS)[number];

/**
 * One area of setup. `count` is what exists (facilities, active service points, active
 * items, …); `missing` is how many of something still need doing (facilities without an
 * MFL code, number formats not set, items without a cash price, payers without a price
 * list). The web turns these into sentences.
 */
export const setupItemSchema = z.object({
  key: z.enum(SETUP_ITEM_KEYS),
  state: z.enum(['done', 'todo']),
  count: z.number().int().nonnegative(),
  missing: z.number().int().nonnegative(),
});

export type SetupItem = z.infer<typeof setupItemSchema>;

export const setupStatusSchema = z.object({ items: z.array(setupItemSchema) });
export type SetupStatusView = z.infer<typeof setupStatusSchema>;

// --- Items without a cash price --------------------------------------------------------------

export const unpricedQuerySchema = z.object({
  limit: z.coerce.number().int().min(0).max(50).default(10),
});

export const unpricedItemsSchema = z.object({
  count: z.number().int().nonnegative(),
  items: z.array(z.object({ id: z.uuid(), code: z.string(), name: z.string() })),
});

export type UnpricedItemsView = z.infer<typeof unpricedItemsSchema>;
